import {
  buildTripFilters,
  tagsToFilters,
} from '@koob/ui/src/features/tripWizard/buildTripPayload';
import type {
  TripTagOption,
  WizardValues,
} from '@koob/ui/src/features/tripWizard/types';
import { v4 as uuidv4 } from 'uuid';

const toDate = (d: unknown) => (d ? new Date(d as string) : null);

const toTags = (list: any[] | undefined): TripTagOption[] =>
  (list ?? []).map((t) => ({ id: t.id, label: t.displayName ?? t.label }));

// The wizard answers a stored trip would give back (edit, or a template
// opened in the designer).
export function seedFromTripFilters(tripFilters: any): Partial<WizardValues> {
  const dmc = tripFilters?.dmcId;
  const isObject = typeof dmc === 'object' && dmc !== null;
  return {
    name: tripFilters?.programName ?? '',
    countries: tripFilters?.countries ?? [],
    dmcId: isObject ? dmc.value : dmc,
    dmcLabel: isObject ? dmc.label : tripFilters?.dmcLabel,
    startLocation: tripFilters?.startLocation,
    endLocation: tripFilters?.endLocation,
    departureDate: toDate(tripFilters?.departureDate),
    duration: tripFilters?.tripDuration ?? 7,
    adultPax: tripFilters?.adultPax ?? 2,
    childrenBirthdates: (tripFilters?.childrenBirthdates ?? []).map(toDate),
    themes: toTags(tripFilters?.themes),
    hotelStyles: toTags(tripFilters?.styles),
    brief: tripFilters?.brief ?? '',
    locale: tripFilters?.locale,
    rooms:
      tripFilters?.composition?.roomsComposition ??
      tripFilters?.roomsComposition ??
      undefined,
  };
}

// A new trip started from a client folder: its pax and birthdates only.
export function seedFromComposition(composition: {
  adults?: number;
  childrenBirthdates?: (string | null)[];
}): Partial<WizardValues> {
  return {
    adultPax: composition.adults ?? 2,
    childrenBirthdates: (composition.childrenBirthdates ?? []).map(toDate),
  };
}

// The tripFilters a saved trip leaves in the store, in the shape the composer
// reads.
export function tripFiltersFromValues(
  values: WizardValues,
  { tripId, locale, dmcId }: { tripId: string; locale: string; dmcId: unknown },
) {
  const { composition, themes, styles, brief } = buildTripFilters(values);
  const childrenCount = composition.children;
  return {
    programName: values.name,
    departureDate: values.departureDate,
    tripDuration: values.duration,
    startLocation: values.startLocation,
    endLocation: values.endLocation,
    countries: values.countries,
    dmcId,
    adultPax: values.adultPax,
    childrenPax: childrenCount,
    childrenBirthdates: values.childrenBirthdates,
    tripComposition: [
      {
        adults: values.adultPax,
        children: childrenCount,
        childrenBirthdates: values.childrenBirthdates,
      },
    ],
    themes,
    styles,
    brief,
    composition,
    locale,
    tripId,
  };
}

const blankDay = (index: number) => ({
  dayIndex: index + 1,
  id: uuidv4(),
  experienceId: null,
  hotelId: null,
  experienceDayIndex: null,
  experiencePricing: null,
  hotelPricing: null,
  hotelBookedOnMyOwn: false,
  selectedExtras: [],
});

// Editing the duration of a composed trip grows or shrinks its days. Trailing
// days holding a product are kept: the composer decides what to do with them.
// Returns the same array when nothing changes.
export function syncDaysWithDuration<
  D extends { experienceId?: unknown; hotelId?: unknown },
>(days: D[], duration: number): D[] {
  if (days.length < duration) {
    const grown = [...days];
    for (let i = days.length; i < duration; i++) {
      grown[i] = blankDay(i) as unknown as D;
    }
    return grown;
  }
  let end = days.length;
  while (
    end > duration &&
    !days[end - 1]?.experienceId &&
    !days[end - 1]?.hotelId
  ) {
    end--;
  }
  return end < days.length ? days.slice(0, end) : days;
}

export { tagsToFilters };
